/**
 * UI copy for every language the site ships in. Spanish is the source of
 * truth; the Italian and English blocks must keep the same keys.
 *
 * Values are plain strings, or functions when a value has to be interpolated.
 * Headlines that set one phrase in italics are split into
 * `[before, emphasis, after]` so the markup stays in the components.
 */
const strings = {
  es: {
    nav: {
      home: "Inicio",
      videos: "Vídeos",
      writing: "Artículos",
      brand: "Girgetto — inicio",
      main: "Navegación principal",
      mobile: "Navegación móvil",
      menu: "Menú",
      openMenu: "Abrir menú",
      closeMenu: "Cerrar menú",
      toLight: "Cambiar a modo claro",
      toDark: "Cambiar a modo oscuro",
      language: "Idioma",
    },
    live: {
      live: "En directo",
      liveNow: "En directo ahora",
      watching: (n) => `${n} viendo`,
      watch: "Ver",
      watchNow: "Ver ahora",
      onTwitch: "En directo en Twitch ahora",
    },
    footer: {
      sign: ["Hecho en público. ", "Ven a verlo."],
      pages: "Páginas",
      elsewhere: "En otros sitios",
      privacy: "Privacidad",
      nav: "Navegación del pie de página",
      builtWith: "Hecho con Gatsby · Inter, Instrument Serif, Lora",
    },
    social: {
      nav: "Redes sociales",
      groups: { code: "Código", gaming: "Gaming" },
      bluesky: "Sígueme en Bluesky",
      instagram: "Sígueme en Instagram (programación)",
      instagramGaming: "Sígueme en Instagram (gaming)",
      twitch: "Mírame en Twitch",
      youtube: "Suscríbete en YouTube (gaming, en español)",
      youtubeItalian: "Suscríbete en YouTube (gaming, en italiano)",
      x: "Sígueme en X",
      itch: "Juega en itch.io",
    },
    home: {
      title: "Inicio",
      description:
        "Girgetto — directos de videojuegos en Twitch y YouTube, creando juegos y herramientas para desarrolladores como iRacing Calendar, y escribiendo sobre JavaScript y desarrollo web.",
      eyebrow: "Girgetto — streamer, desarrollador y escritor",
      headline: [
        "Hago directos de ",
        "videojuegos",
        ", programo y creo juegos y herramientas por el camino.",
      ],
      lede: "En directo en Twitch jugando, con lo mejor en YouTube después. Fuera del directo soy programador: creo juegos y proyectos como iRacing Calendar, y escribo aquí lo que voy aprendiendo.",
    },
    projects: {
      label: "Proyectos",
      allOnItch: "Todo en itch.io",
      iracingDescription:
        "Todas las series de iRacing, semana a semana: circuitos, sesiones y horarios de carrera, actualizado automáticamente cada temporada.",
      iracingMeta: "Aplicación web · Código abierto",
      gamesFallback: "La lista de juegos no carga ahora mismo; está todo en ",
      coverArt: (title) => `Portada de ${title}`,
    },
    videoStrip: {
      heading: "Últimos vídeos",
      allVideos: "Todos los vídeos",
      fallback: ["Los últimos vídeos no cargan ahora mismo; están todos en ", " y ", "."],
    },
    writingIndex: {
      label: "Artículos",
      allPosts: "Todos los artículos",
    },
    videos: {
      title: "Vídeos",
      description:
        "Mira mis últimos vídeos de YouTube y directos de Twitch: programación en directo, tutoriales y mucho más.",
      eyebrow: "Vídeos y directos",
      headline: ["Todo lo que he ", "creado ante la cámara", "."],
      lede: "Programación en directo, tutoriales y alguna que otra game jam: en directo en Twitch y archivado en YouTube.",
      subscribeYouTube: "Suscríbete en YouTube",
      followTwitch: "Sígueme en Twitch",
      twitchHeading: "Últimos directos de Twitch",
      follow: "Seguir",
      youtubeHeading: "Últimos vídeos de YouTube",
      openPlaylist: "Abrir lista",
      watchOn: (title, platform) => `Ver ${title} en ${platform}`,
      fallback: "Los últimos vídeos no cargan ahora mismo.",
      watchOnYouTube: "Ver en YouTube",
      watchOnTwitch: "Ver en Twitch",
    },
    writing: {
      title: "Artículos",
      description:
        "Notas sobre JavaScript, desarrollo web y la creación de pequeños juegos.",
      eyebrow: "Artículos",
      headline: ["Notas sobre ", "JavaScript", ", la web y cómo crear cosas pequeñas."],
      lede: "Artículos largos sobre las cosas que merece la pena contar. Algunos también están en italiano o en inglés.",
      translations: "Traducciones disponibles",
    },
    post: {
      tableOfContents: "Tabla de contenidos",
      previous: "Anterior",
      next: "Siguiente",
      minRead: "min de lectura",
      backToWriting: "Artículos",
      updated: "Actualizado",
      readIn: "Leer en",
      aboutAuthor: "Sobre el autor",
      authorBio:
        "Soy Girgetto: hago directos jugando a videojuegos en Twitch y subo lo mejor a YouTube. Fuera del directo soy programador: creo juegos y proyectos como iRacing Calendar, y escribo aquí lo que voy aprendiendo.",
      watchTwitch: "Sígueme en Twitch",
      watchYouTube: "Suscríbete en YouTube",
      seeProject: "iRacing Calendar",
      home: "Más sobre mí",
    },
    privacy: {
      title: "Política de privacidad",
      description:
        "Política de privacidad de girgetto.it: cómo se tratan tus datos y se protege tu privacidad.",
      eyebrow: "Legal",
      heading: "Política de privacidad",
      version: (v) => `Versión ${v}`,
      effective: (date) => `En vigor desde el ${date}`,
      current: "Actual",
      versionHistory: "Historial de versiones",
      currentPolicy: "Política de privacidad actual",
      currentVersion: "Versión actual",
      archived: "Legal · Archivada",
      versionHeading: (v) => `Política de privacidad, versión ${v}`,
      versionTitle: (v) => `Política de privacidad — versión ${v}`,
      versionDescription: (v, date) =>
        `Política de privacidad, versión ${v}, en vigor desde el ${date}.`,
      historyTitle: "Historial de la política de privacidad",
      historyDescription:
        "Todas las versiones publicadas de la política de privacidad de girgetto.it.",
      historyLede:
        "Todas las versiones publicadas de la política de privacidad, de la más reciente a la más antigua.",
      notFoundTitle: "Versión de la política de privacidad no encontrada",
      notFoundDescription:
        "No se ha encontrado la versión solicitada de la política de privacidad.",
      notFoundHeadline: ["Esa versión ", "no está aquí", "."],
      notFoundLede:
        "No se ha encontrado la versión de la política de privacidad que buscabas.",
    },
    notFound: {
      title: "404 — Página no encontrada",
      description: "La página que buscabas no existe.",
      eyebrow: "Error 404",
      headline: ["Esto ", "no está aquí", "."],
      lede: "La página que buscabas no existe: puede que se haya movido o que nunca haya existido.",
      back: "Volver al inicio",
      watch: "Mejor mira un vídeo",
    },
  },

  it: {
    nav: {
      home: "Home",
      videos: "Video",
      writing: "Articoli",
      brand: "Girgetto — home",
      main: "Navigazione principale",
      mobile: "Navigazione mobile",
      menu: "Menu",
      openMenu: "Apri il menu",
      closeMenu: "Chiudi il menu",
      toLight: "Passa alla modalità chiara",
      toDark: "Passa alla modalità scura",
      language: "Lingua",
    },
    live: {
      live: "In diretta",
      liveNow: "In diretta ora",
      watching: (n) => `${n} spettatori`,
      watch: "Guarda",
      watchNow: "Guarda ora",
      onTwitch: "In diretta su Twitch ora",
    },
    footer: {
      sign: ["Fatto in pubblico. ", "Vieni a guardare."],
      pages: "Pagine",
      elsewhere: "Altrove",
      privacy: "Privacy",
      nav: "Navigazione del piè di pagina",
      builtWith: "Realizzato con Gatsby · Inter, Instrument Serif, Lora",
    },
    social: {
      nav: "Social network",
      groups: { code: "Coding", gaming: "Gaming" },
      bluesky: "Seguimi su Bluesky",
      instagram: "Seguimi su Instagram (coding)",
      instagramGaming: "Seguimi su Instagram (gaming)",
      twitch: "Guardami su Twitch",
      youtube: "Iscriviti su YouTube (gaming, in spagnolo)",
      youtubeItalian: "Iscriviti su YouTube (gaming, in italiano)",
      x: "Seguimi su X",
      itch: "Gioca su itch.io",
    },
    home: {
      title: "Home",
      description:
        "Girgetto — dirette di videogiochi su Twitch e YouTube, giochi e strumenti per sviluppatori come iRacing Calendar, e articoli su JavaScript e sviluppo web.",
      eyebrow: "Girgetto — streamer, sviluppatore e scrittore",
      headline: [
        "Faccio dirette di ",
        "videogiochi",
        ", scrivo codice e creo giochi e strumenti lungo la strada.",
      ],
      lede: "In diretta su Twitch a giocare, con i momenti migliori su YouTube. Fuori dalle dirette sono un programmatore: creo giochi e progetti come iRacing Calendar, e qui scrivo quello che imparo.",
    },
    projects: {
      label: "Progetti",
      allOnItch: "Tutto su itch.io",
      iracingDescription:
        "Tutte le serie di iRacing, settimana per settimana: circuiti, sessioni e orari di gara, aggiornati automaticamente a ogni stagione.",
      iracingMeta: "Web app · Open source",
      gamesFallback: "L'elenco dei giochi non si sta caricando; trovi tutto su ",
      coverArt: (title) => `Copertina di ${title}`,
    },
    videoStrip: {
      heading: "Ultimi video",
      allVideos: "Tutti i video",
      fallback: ["Gli ultimi video non si stanno caricando; li trovi tutti su ", " e ", "."],
    },
    writingIndex: {
      label: "Articoli",
      allPosts: "Tutti gli articoli",
    },
    videos: {
      title: "Video",
      description:
        "Guarda i miei ultimi video su YouTube e le dirette su Twitch: live coding, tutorial e molto altro.",
      eyebrow: "Video e dirette",
      headline: ["Tutto quello che ho ", "creato davanti alla telecamera", "."],
      lede: "Live coding, tutorial e qualche game jam: in diretta su Twitch e archiviati su YouTube.",
      subscribeYouTube: "Iscriviti su YouTube",
      followTwitch: "Seguimi su Twitch",
      twitchHeading: "Ultime dirette su Twitch",
      follow: "Segui",
      youtubeHeading: "Ultimi video su YouTube",
      openPlaylist: "Apri la playlist",
      watchOn: (title, platform) => `Guarda ${title} su ${platform}`,
      fallback: "Gli ultimi video non si stanno caricando.",
      watchOnYouTube: "Guarda su YouTube",
      watchOnTwitch: "Guarda su Twitch",
    },
    writing: {
      title: "Articoli",
      description:
        "Appunti su JavaScript, sviluppo web e creazione di piccoli giochi.",
      eyebrow: "Articoli",
      headline: ["Appunti su ", "JavaScript", ", il web e come creare piccole cose."],
      lede: "Articoli approfonditi sulle cose che vale la pena raccontare. Quelli non ancora tradotti in italiano sono in spagnolo.",
      translations: "Traduzioni disponibili",
    },
    post: {
      tableOfContents: "Indice dei contenuti",
      previous: "Precedente",
      next: "Successivo",
      minRead: "min di lettura",
      backToWriting: "Articoli",
      updated: "Aggiornato",
      readIn: "Leggi in",
      aboutAuthor: "Sull'autore",
      authorBio:
        "Sono Girgetto: faccio dirette di videogiochi su Twitch e pubblico i momenti migliori su YouTube. Fuori dalle dirette sono un programmatore: creo giochi e progetti come iRacing Calendar, e qui scrivo quello che imparo.",
      watchTwitch: "Seguimi su Twitch",
      watchYouTube: "Iscriviti su YouTube",
      seeProject: "iRacing Calendar",
      home: "Altro su di me",
    },
    privacy: {
      title: "Informativa sulla privacy",
      description:
        "Informativa sulla privacy di girgetto.it: come vengono trattati i tuoi dati e protetta la tua privacy.",
      eyebrow: "Note legali",
      heading: "Informativa sulla privacy",
      version: (v) => `Versione ${v}`,
      effective: (date) => `In vigore dal ${date}`,
      current: "Attuale",
      versionHistory: "Cronologia delle versioni",
      currentPolicy: "Informativa sulla privacy attuale",
      currentVersion: "Versione attuale",
      archived: "Note legali · Archiviata",
      versionHeading: (v) => `Informativa sulla privacy, versione ${v}`,
      versionTitle: (v) => `Informativa sulla privacy — versione ${v}`,
      versionDescription: (v, date) =>
        `Informativa sulla privacy, versione ${v}, in vigore dal ${date}.`,
      historyTitle: "Cronologia dell'informativa sulla privacy",
      historyDescription:
        "Tutte le versioni pubblicate dell'informativa sulla privacy di girgetto.it.",
      historyLede:
        "Tutte le versioni pubblicate dell'informativa sulla privacy, dalla più recente.",
      notFoundTitle: "Versione dell'informativa non trovata",
      notFoundDescription:
        "La versione richiesta dell'informativa sulla privacy non è stata trovata.",
      notFoundHeadline: ["Quella versione ", "non c'è", "."],
      notFoundLede:
        "La versione dell'informativa sulla privacy che cercavi non è stata trovata.",
    },
    notFound: {
      title: "404 — Pagina non trovata",
      description: "La pagina che cercavi non esiste.",
      eyebrow: "Errore 404",
      headline: ["Questa pagina ", "non c'è", "."],
      lede: "La pagina che cercavi non esiste: potrebbe essere stata spostata, o non essere mai esistita.",
      back: "Torna alla home",
      watch: "Guarda un video invece",
    },
  },

  en: {
    nav: {
      home: "Index",
      videos: "Videos",
      writing: "Writing",
      brand: "Girgetto — home",
      main: "Main navigation",
      mobile: "Mobile navigation",
      menu: "Menu",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      toLight: "Switch to light mode",
      toDark: "Switch to dark mode",
      language: "Language",
    },
    live: {
      live: "Live",
      liveNow: "Live now",
      watching: (n) => `${n} watching`,
      watch: "Watch",
      watchNow: "Watch now",
      onTwitch: "Currently live on Twitch",
    },
    footer: {
      sign: ["Made in public. ", "Come watch."],
      pages: "Pages",
      elsewhere: "Elsewhere",
      privacy: "Privacy",
      nav: "Footer navigation",
      builtWith: "Built with Gatsby · Inter, Instrument Serif, Lora",
    },
    social: {
      nav: "Social media links",
      groups: { code: "Code", gaming: "Gaming" },
      bluesky: "Follow on Bluesky",
      instagram: "Follow on Instagram (coding)",
      instagramGaming: "Follow on Instagram (gaming)",
      twitch: "Watch on Twitch",
      youtube: "Subscribe on YouTube (gaming, in Spanish)",
      youtubeItalian: "Subscribe on YouTube (gaming, in Italian)",
      x: "Follow on X",
      itch: "Play on itch.io",
    },
    home: {
      title: "Home",
      description:
        "Girgetto — streaming video games on Twitch and YouTube, building games and developer tools like iRacing Calendar, and writing about JavaScript and web development.",
      eyebrow: "Girgetto — streamer, developer & writer",
      headline: [
        "I stream ",
        "video games",
        ", write code, and build games and tools along the way.",
      ],
      lede: "Live on Twitch playing games, with the best bits on YouTube afterwards. Off stream I'm a programmer building games and side projects like iRacing Calendar — and writing up what I learn here.",
    },
    projects: {
      label: "Projects",
      allOnItch: "All on itch.io",
      iracingDescription:
        "Every iRacing series, week by week: tracks, sessions and race times, updated automatically each season.",
      iracingMeta: "Web app · Open source",
      gamesFallback: "The games list isn’t loading right now — everything is on ",
      coverArt: (title) => `${title} cover art`,
    },
    videoStrip: {
      heading: "Latest videos",
      allVideos: "All videos",
      fallback: ["Latest uploads aren’t loading right now — they’re all on ", " and ", "."],
    },
    writingIndex: {
      label: "Writing",
      allPosts: "All posts",
    },
    videos: {
      title: "Videos",
      description:
        "Watch my latest YouTube videos and Twitch streams. Live coding, tutorials, and more.",
      eyebrow: "Videos & streams",
      headline: ["Everything I’ve ", "built on camera", "."],
      lede: "Live coding, tutorials and the occasional game jam — streamed on Twitch, archived on YouTube.",
      subscribeYouTube: "Subscribe on YouTube",
      followTwitch: "Follow on Twitch",
      twitchHeading: "Latest Twitch streams",
      follow: "Follow",
      youtubeHeading: "Latest YouTube videos",
      openPlaylist: "Open playlist",
      watchOn: (title, platform) => `Watch ${title} on ${platform}`,
      fallback: "The latest videos aren’t loading right now.",
      watchOnYouTube: "Watch on YouTube",
      watchOnTwitch: "Watch on Twitch",
    },
    writing: {
      title: "Writing",
      description: "Notes on JavaScript, web development and building small games.",
      eyebrow: "Writing",
      headline: ["Notes on ", "JavaScript", ", the web, and making small things."],
      lede: "Long-form write-ups of the things worth writing up. Posts not yet translated into English are shown in Spanish.",
      translations: "Available translations",
    },
    post: {
      tableOfContents: "Table of Contents",
      previous: "Previous",
      next: "Next",
      minRead: "min read",
      backToWriting: "Writing",
      updated: "Updated",
      readIn: "Read in",
      aboutAuthor: "About the author",
      authorBio:
        "I'm Girgetto: I stream video games live on Twitch and post the best bits on YouTube. Off stream I'm a programmer building games and side projects like iRacing Calendar, and writing up what I learn here.",
      watchTwitch: "Follow on Twitch",
      watchYouTube: "Subscribe on YouTube",
      seeProject: "iRacing Calendar",
      home: "More about me",
    },
    privacy: {
      title: "Privacy Policy",
      description:
        "Privacy policy for girgetto.it - Learn how we handle your data and protect your privacy.",
      eyebrow: "Legal",
      heading: "Privacy policy",
      version: (v) => `Version ${v}`,
      effective: (date) => `Effective ${date}`,
      current: "Current",
      versionHistory: "Version history",
      currentPolicy: "Current privacy policy",
      currentVersion: "Current version",
      archived: "Legal · Archived",
      versionHeading: (v) => `Privacy policy, version ${v}`,
      versionTitle: (v) => `Privacy Policy — Version ${v}`,
      versionDescription: (v, date) =>
        `Privacy policy version ${v} — effective ${date}`,
      historyTitle: "Privacy Policy Version History",
      historyDescription:
        "Every published version of the girgetto.it privacy policy.",
      historyLede: "Every published version of the privacy policy, newest first.",
      notFoundTitle: "Privacy Policy Version Not Found",
      notFoundDescription:
        "The requested privacy policy version could not be found.",
      notFoundHeadline: ["That version ", "isn’t here", "."],
      notFoundLede: "The privacy policy version you asked for could not be found.",
    },
    notFound: {
      title: "404 — Page not found",
      description: "The page you were looking for doesn't exist.",
      eyebrow: "Error 404",
      headline: ["This one’s ", "not here", "."],
      lede: "The page you were looking for doesn’t exist — it may have moved, or it may never have existed at all.",
      back: "Back to the index",
      watch: "Watch something instead",
    },
  },
};

export default strings;
